import sortBy from "lodash/sortBy";
import { IFacet, IFacetGroup } from "shared/models/facet";
import { IAgencyPermission } from "shared/services/api/agents/agentsService";
import { IMakeSuggestionsFilterParams } from "models/api";
import { IMakeSuggestionsPerformer } from "models/suggestion";
import {
  IMakeSuggestionRequestParams,
  IPatchSuggestionEditMap,
} from "services/api/suggestions/suggestionsService.types";
import { IDropdownCheckboxValueItemConfig } from "component-library";

export function filterByAgency(
  performers: IMakeSuggestionsPerformer[],
  agents?: string[]
): IMakeSuggestionsPerformer[] {
  if (performers.length === 0) {
    return [];
  }

  if (!agents) {
    return performers;
  }

  return performers.filter((item) => agents.includes(item.agency.id));
}

export function filterByGender(
  performers: IMakeSuggestionsPerformer[],
  genders?: number[]
): IMakeSuggestionsPerformer[] {
  if (performers.length === 0) {
    return [];
  }

  if (!genders) {
    return performers;
  }

  return performers.filter((item) =>
    item.genders?.some((itemGender) => genders.includes(itemGender))
  );
}

export function filterByAppearance(
  performers: IMakeSuggestionsPerformer[],
  appearances?: number[]
): IMakeSuggestionsPerformer[] {
  if (performers.length === 0) {
    return [];
  }

  if (!appearances) {
    return performers;
  }

  return performers.filter((item) =>
    item.appearances?.some((itemAppearance) =>
      appearances.includes(itemAppearance)
    )
  );
}

export function filterByText(
  performers: IMakeSuggestionsPerformer[],
  searchString?: string
): IMakeSuggestionsPerformer[] {
  if (performers.length === 0) {
    return [];
  }

  if (!searchString) {
    return performers;
  }

  return performers.filter((item) => {
    const text = `${item.forenames} ${item.surname}`.toLocaleLowerCase();
    const search = searchString?.toLocaleLowerCase().split(" ");

    return search?.some((word) => text.includes(word));
  });
}

export function findFilteredResults(
  suggestedPerformers: IMakeSuggestionsPerformer[],
  filters: IMakeSuggestionsFilterParams
): IMakeSuggestionsPerformer[] {
  return filterByAppearance(
    filterByText(
      filterByGender(
        filterByAgency(suggestedPerformers.slice(), filters.agents),
        filters.gender
      ),
      filters.textFilter
    ),
    filters.appearances
  );
}

export function mapFacetCheckboxOptions(
  items: IFacet[] = [],
  options?: {
    isCollapsible?: boolean;
    unwrapGroupCodes?: string[];
    sort?: boolean;
  }
): IDropdownCheckboxValueItemConfig[] {
  const mappedItems = items.reduce((result, item) => {
    result.push({
      label: item.name,
      value: String(item.id),
      selectedLabel: "",
    });

    return result;
  }, [] as IDropdownCheckboxValueItemConfig[]);

  return options?.sort
    ? sortBy(mappedItems, [(subItem) => subItem.label])
    : mappedItems;
}

export function mapToCheckBoxItem(
  name: string,
  id: number
): IDropdownCheckboxValueItemConfig {
  return {
    label: name,
    value: String(id),
    selectedLabel: "",
  };
}

export function mapAgencyListCheckboxOptions(
  items: IAgencyPermission[] = []
): IDropdownCheckboxValueItemConfig[] {
  return items.map((item) => mapToCheckBoxItem(item.name, item.id));
}

export function getCreatedData(
  performers: IMakeSuggestionsPerformer[]
): IMakeSuggestionRequestParams[] {
  return performers.reduce(
    (
      createdList: IMakeSuggestionRequestParams[],
      item: IMakeSuggestionsPerformer
    ) =>
      item.suggestion!.id
        ? createdList
        : [
            ...createdList,
            {
              artist: item.artistRef,
              photo: item.suggestion!.photo,
              comment: item.suggestion!.comment,
            },
          ],
    []
  );
}

export function filterEditedData(
  removedIds: string[],
  editedSuggestions: IPatchSuggestionEditMap
): IPatchSuggestionEditMap {
  removedIds.forEach((id) => {
    if (Object.hasOwn(editedSuggestions, id)) {
      delete editedSuggestions[id];
    }
  });

  return editedSuggestions;
}

export function mapFacetListCheckboxOptions(
  items: IFacet[] = []
): IDropdownCheckboxValueItemConfig[] {
  return items.map((item) => mapToCheckBoxItem(item.name, item.id));
}

export function mapFacetGroupListCheckboxOptions(
  items: IFacetGroup[] = []
): IDropdownCheckboxValueItemConfig[] {
  const facetList = items.map((group) => group.facets);

  return mapFacetListCheckboxOptions(([] as IFacet[]).concat(...facetList));
}
